import { useCallback } from "react";
import { useXPHistoryStore } from "../stores/xpHistoryStore";
import { useSDKConfigStore } from "../stores/sdkConfigStore";
import { getXPHistory } from "../api/rewards/api";
import { XPHistoryEntry } from "../api/rewards/types";

const PAGE_LIMIT = 20;

interface UseXPHistoryReturn {
  entries: XPHistoryEntry[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  totalCount: number;
  loadMore: () => Promise<void>;
  fetch: () => Promise<void>;
  refetch: () => Promise<void>;
  reset: () => void;
}

/**
 * Hook for managing XP history with pagination
 * Only fetches when explicitly called (lazy loading)
 * Caches results in store to prevent refetching
 */
export const useXPHistory = (): UseXPHistoryReturn => {
  const {
    entries,
    totalCount,
    hasMore,
    currentOffset,
    isLoading,
    isLoadingMore,
    error,
    _setEntries,
    _appendEntries,
    _setTotalCount,
    _setHasMore,
    _setCurrentOffset,
    _setLoading,
    _setLoadingMore,
    _setError,
    _reset,
  } = useXPHistoryStore();

  /**
   * Initial fetch of XP history
   */
  const fetch = useCallback(
    async (isRefetch: boolean = false) => {
      const { isInitialized } = useSDKConfigStore.getState();

      if (!isInitialized) {
        console.error("❌ SDK: Cannot fetch XP history - SDK not initialized");
        return;
      }

      // If already has data, don't refetch (cache)
      if (entries.length > 0 && !isRefetch) {
        console.log("✅ SDK: Using cached XP history data");
        return;
      }

      _setLoading(true);
      _setError(null);

      try {
        const response = await getXPHistory({
          limit: PAGE_LIMIT,
          offset: 0,
        });

        _setEntries(response.data.entries);
        _setTotalCount(response.data.totalCount);
        _setHasMore(response.data.hasMore);
        _setCurrentOffset(response.data.entries.length);

        console.log("✅ SDK: Successfully fetched XP history:", {
          count: response.data.entries.length,
          totalCount: response.data.totalCount,
          hasMore: response.data.hasMore,
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to fetch XP history";
        console.error("❌ SDK: Error fetching XP history:", errorMessage);
        _setError(errorMessage);
      } finally {
        _setLoading(false);
      }
    },
    [
      entries.length,
      _setLoading,
      _setError,
      _setEntries,
      _setTotalCount,
      _setHasMore,
      _setCurrentOffset,
    ]
  );

  /**
   * Load more XP history entries (pagination)
   */
  const loadMore = useCallback(async () => {
    const { isInitialized } = useSDKConfigStore.getState();

    if (!isInitialized) {
      console.error(
        "❌ SDK: Cannot load more XP history - SDK not initialized"
      );
      return;
    }

    if (!hasMore || isLoadingMore) {
      return;
    }

    _setLoadingMore(true);
    _setError(null);

    try {
      const response = await getXPHistory({
        limit: PAGE_LIMIT,
        offset: currentOffset,
      });

      _appendEntries(response.data.entries);
      _setHasMore(response.data.hasMore);
      _setCurrentOffset(currentOffset + response.data.entries.length);

      console.log("✅ SDK: Successfully loaded more XP history:", {
        newCount: response.data.entries.length,
        totalLoaded: currentOffset + response.data.entries.length,
        hasMore: response.data.hasMore,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to load more XP history";
      console.error("❌ SDK: Error loading more XP history:", errorMessage);
      _setError(errorMessage);
    } finally {
      _setLoadingMore(false);
    }
  }, [
    hasMore,
    isLoadingMore,
    entries,
    currentOffset,
    _setLoadingMore,
    _setError,
    _appendEntries,
    _setHasMore,
    _setCurrentOffset,
  ]);

  /**
   * Reset XP history state (clears cache)
   */
  const reset = useCallback(() => {
    _reset();
  }, [_reset]);

  return {
    entries,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    totalCount,
    loadMore,
    fetch,
    refetch: () => fetch(true),
    reset,
  };
};
